'use client'

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { MdLocalHospital, MdSave, MdArrowBack } from 'react-icons/md'
import {
    getUnit,
    createUnit,
    updateUnit,
    CreateUnitData,
    HospitalUnitType,
    UNIT_TYPE_LABELS,
} from '@/lib/api/hospital'
import { HospitalSelector } from '@/components/HospitalSelector'
import { useHospitals } from '@/hooks/useHospitals'

export default function UnitFormPage() {
    const navigate = useNavigate()
    const { id } = useParams()
    const isEditing = Boolean(id)

    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const { hospitals, loading: loadingHospitals, selectedHospitalId, setSelected } = useHospitals()
    const [formData, setFormData] = useState<CreateUnitData>({
        hospitalId: 0,
        name: '',
        code: '',
        type: 'WARD',
        floor: '',
        wing: '',
        requiresIsolation: false,
        nursingStationPhone: '',
    })
    const [active, setActive] = useState(true)

    useEffect(() => {
        if (!isEditing && selectedHospitalId && formData.hospitalId !== selectedHospitalId) {
            setFormData(prev => ({ ...prev, hospitalId: selectedHospitalId }))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedHospitalId, isEditing])

    useEffect(() => {
        if (isEditing && id) {
            loadUnit(parseInt(id))
        }
    }, [id, isEditing])

    const loadUnit = async (unitId: number) => {
        setLoading(true)
        try {
            const response = await getUnit(unitId)
            if (response.success) {
                const unit = response.result
                setFormData({
                    hospitalId: unit.hospitalId,
                    name: unit.name,
                    code: unit.code || '',
                    type: unit.type,
                    floor: unit.floor || '',
                    wing: unit.wing || '',
                    requiresIsolation: unit.requiresIsolation,
                    nursingStationPhone: unit.nursingStationPhone || '',
                })
                setActive(unit.active)
            } else {
                toast.error(response.error || 'Erro ao carregar unidade')
                navigate('/hospital/unidades')
            }
        } catch (error) {
            toast.error('Erro ao carregar unidade')
            navigate('/hospital/unidades')
        } finally {
            setLoading(false)
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!formData.name.trim()) {
            toast.error('Nome é obrigatório')
            return
        }
        if (!formData.hospitalId) {
            toast.error('Selecione o hospital')
            return
        }

        setSaving(true)
        try {
            const data = {
                ...formData,
                code: formData.code || undefined,
                floor: formData.floor || undefined,
                wing: formData.wing || undefined,
                nursingStationPhone: formData.nursingStationPhone || undefined,
            }

            let response
            if (isEditing && id) {
                response = await updateUnit(parseInt(id), { ...data, active })
            } else {
                response = await createUnit(data)
            }

            if (response.success) {
                toast.success(
                    isEditing ? 'Unidade atualizada com sucesso' : 'Unidade criada com sucesso'
                )
                navigate('/hospital/unidades')
            } else {
                toast.error(response.error || 'Erro ao salvar unidade')
            }
        } catch (error) {
            toast.error('Erro ao salvar unidade')
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando unidade...</p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/hospital/unidades')}
                        className="btn btn-ghost btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdLocalHospital className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            {isEditing ? 'Editar Unidade' : 'Nova Unidade'}
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {isEditing
                                ? 'Atualize os dados da unidade'
                                : 'Cadastre uma nova unidade hospitalar'}
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">Hospital</h2>
                        <p className="text-sm text-base-content/60 mb-3">
                            Selecione o hospital ao qual essa unidade pertence
                        </p>
                        <HospitalSelector
                            hospitals={hospitals}
                            selectedId={formData.hospitalId || null}
                            onChange={id => {
                                setFormData({ ...formData, hospitalId: id })
                                setSelected(id)
                            }}
                            loading={loadingHospitals}
                        />
                    </div>
                </div>

                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">Informações Básicas</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Nome <span className="text-error">*</span>
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="Ex: UTI Adulto"
                                    value={formData.name}
                                    onChange={e =>
                                        setFormData({ ...formData, name: e.target.value })
                                    }
                                    required
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Código</span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="Ex: UTI-01"
                                    value={formData.code}
                                    onChange={e =>
                                        setFormData({ ...formData, code: e.target.value })
                                    }
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Tipo <span className="text-error">*</span>
                                    </span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={formData.type}
                                    onChange={e =>
                                        setFormData({
                                            ...formData,
                                            type: e.target.value as HospitalUnitType,
                                        })
                                    }
                                    required
                                >
                                    {Object.entries(UNIT_TYPE_LABELS).map(([key, label]) => (
                                        <option key={key} value={key}>
                                            {label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Telefone do Posto</span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="Ex: (11) 3333-4444"
                                    value={formData.nursingStationPhone}
                                    onChange={e =>
                                        setFormData({
                                            ...formData,
                                            nursingStationPhone: e.target.value,
                                        })
                                    }
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">Localização</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Andar</span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="Ex: 3"
                                    value={formData.floor}
                                    onChange={e =>
                                        setFormData({ ...formData, floor: e.target.value })
                                    }
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Ala</span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="Ex: A"
                                    value={formData.wing}
                                    onChange={e =>
                                        setFormData({ ...formData, wing: e.target.value })
                                    }
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">Configurações</h2>
                        <div className="space-y-4">
                            <div className="form-control">
                                <label className="label cursor-pointer justify-start gap-4">
                                    <input
                                        type="checkbox"
                                        className="checkbox checkbox-secondary"
                                        checked={formData.requiresIsolation}
                                        onChange={e =>
                                            setFormData({
                                                ...formData,
                                                requiresIsolation: e.target.checked,
                                            })
                                        }
                                    />
                                    <div className="flex flex-col">
                                        <span className="label-text font-medium">
                                            Requer Isolamento
                                        </span>
                                        <span className="text-xs text-base-content/60">
                                            Unidade com protocolos especiais de isolamento
                                        </span>
                                    </div>
                                </label>
                            </div>

                            {isEditing && (
                                <div className="form-control">
                                    <label className="label cursor-pointer justify-start gap-4">
                                        <input
                                            type="checkbox"
                                            className="checkbox checkbox-secondary"
                                            checked={active}
                                            onChange={e => setActive(e.target.checked)}
                                        />
                                        <div className="flex flex-col">
                                            <span className="label-text font-medium">Ativa</span>
                                            <span className="text-xs text-base-content/60">
                                                Unidade disponível para internações
                                            </span>
                                        </div>
                                    </label>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => navigate('/hospital/unidades')}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={saving}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-sm"></span>
                        ) : (
                            <MdSave className="text-xl" />
                        )}
                        {isEditing ? 'Atualizar' : 'Criar'}
                    </button>
                </div>
            </form>
        </div>
    )
}
